import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import PhotoUpload from "../components/app/PhotoUpload";
import LocationPicker from "../components/app/LocationPicker";
import { Save, MapPin, Phone, Users, User, Settings as SettingsIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { id: "profile", label_bg: "Профил", label_en: "Profile", icon: User },
  { id: "preferences", label_bg: "Предпочитания", label_en: "Preferences", icon: SettingsIcon },
  { id: "location", label_bg: "Локация", label_en: "Location", icon: MapPin },
];

export default function ParentSettings() {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("profile");
  const [childCount, setChildCount] = useState(0);

  useEffect(() => {
    api.get("/auth/me").then((r) => setForm(r.data));
    api.get("/children").then((r) => setChildCount(r.data.length)).catch(() => {});
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", {
        name: form.name,
        city: form.city,
        photo_url: form.photo_url,
        phone: form.phone,
        location_lat: form.location_lat ?? null,
        location_lng: form.location_lng ?? null,
      });
      if (setUser) setUser(data);
      toast.success(t("saved"));
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Error");
    }
    setSaving(false);
  };

  const f = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <Layout>
      <div className="max-w-5xl space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Настройки" : "Settings"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
              {lang === "bg" ? "Моят профил" : "My profile"}
            </h1>
          </div>
          <button type="button" onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 flex items-center gap-2" data-testid="parent-save-top">
            <Save className="w-4 h-4" /> {saving ? "..." : t("save")}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <form onSubmit={save} className="space-y-5">
            {/* Section tabs */}
            <div className="flex flex-wrap gap-2 border-b border-white/8 pb-3">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActive(s.id)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
                    active === s.id ? "text-pitch border-b-2 border-pitch -mb-3 pb-3" : "text-white/45 hover:text-white"
                  }`}
                  data-testid={`parent-tab-${s.id}`}
                >
                  <s.icon className="w-3.5 h-3.5" /> {lang === "bg" ? s.label_bg : s.label_en}
                </button>
              ))}
            </div>

            {/* Profile */}
            {active === "profile" && (
              <div className="surface-elevated p-6 flex flex-col sm:flex-row gap-6 items-start animate-fade-up" style={{ opacity: 0 }}>
                <div className="flex flex-col items-center gap-2">
                  <PhotoUpload value={form.photo_url} onChange={f("photo_url")} size={120} testId="parent-photo" />
                  <span className="label-mono">{lang === "bg" ? "Профилна снимка" : "Profile photo"}</span>
                </div>
                <div className="flex-1 space-y-3 w-full">
                  <Field label={t("name")} v={form.name} on={f("name")} testId="parent-name" />
                  <Field label={lang === "bg" ? "Имейл" : "Email"} v={form.email} on={() => {}} testId="parent-email" disabled />
                  <Field label={lang === "bg" ? "Телефон" : "Phone"} v={form.phone} on={f("phone")} testId="parent-phone" icon={Phone} placeholder="+359..." />
                </div>
              </div>
            )}

            {/* Preferences */}
            {active === "preferences" && (
              <div className="surface-elevated p-6 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
                <p className="text-xs text-white/50 font-mono">
                  {lang === "bg"
                    ? "Тези данни помагат на треньорите да предложат най-подходящите сесии за детето ти."
                    : "These details help coaches recommend the best sessions for your child."}
                </p>
                <Field label={lang === "bg" ? "Град" : "City"} v={form.city} on={f("city")} testId="parent-city" icon={MapPin} />
                <div>
                  <label className="label-mono block mb-1.5">{lang === "bg" ? "Деца" : "Children"}</label>
                  <Link to="/children" className="surface-1 p-4 flex items-center justify-between hover:border-pitch transition-colors group" data-testid="parent-children-link">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-pitch/15 border border-pitch/30 flex items-center justify-center text-pitch"><Users className="w-4 h-4" /></div>
                      <div>
                        <div className="font-oswald uppercase text-base tracking-tight">{childCount} {lang === "bg" ? "записани" : "registered"}</div>
                        <div className="text-xs text-white/45 font-mono">{lang === "bg" ? "Добави, редактирай и проследи" : "Add, edit and track"}</div>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-pitch group-hover:translate-x-1 transition-all" />
                  </Link>
                </div>
              </div>
            )}

            {/* Location */}
            {active === "location" && (
              <div className="surface-elevated p-6 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
                <p className="text-xs text-white/50 font-mono">
                  {lang === "bg"
                    ? "Маркирай предпочитаната локация за тренировки. Треньори ще те виждат на картата „Треньори около теб“."
                    : "Pin your preferred training location. Coaches will see you on the „Coaches near you“ map."}
                </p>
                <LocationPicker
                  lat={typeof form.location_lat === "number" ? form.location_lat : null}
                  lng={typeof form.location_lng === "number" ? form.location_lng : null}
                  onChange={(pos) => setForm((p) => ({ ...p, location_lat: pos?.lat ?? null, location_lng: pos?.lng ?? null }))}
                />
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="label-mono">LAT</span>
                  <span className="text-white/55">{form.location_lat?.toFixed?.(5) ?? "—"}</span>
                  <span className="label-mono ml-3">LNG</span>
                  <span className="text-white/55">{form.location_lng?.toFixed?.(5) ?? "—"}</span>
                  {(form.location_lat || form.location_lng) && (
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, location_lat: null, location_lng: null }))}
                      className="ml-auto text-red-400 hover:text-red-300"
                      data-testid="parent-clear-location"
                    >
                      {lang === "bg" ? "Изчисти" : "Clear"}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="parent-save">
                <Save className="w-4 h-4" /> {t("save")}
              </button>
            </div>
          </form>

          {/* Right column: live preview */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="label-mono mb-2">// {lang === "bg" ? "Преглед" : "Preview"}</div>
            <div className="surface-elevated p-5">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 flex-shrink-0">
                  {form.photo_url ? (
                    <img
                      src={form.photo_url + (form.photo_url.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token")}` : "")}
                      className="w-full h-full object-cover rounded-full border-2 border-pitch/40"
                      alt=""
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{form.name?.[0] || "P"}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-oswald uppercase text-lg tracking-tight truncate">{form.name || "—"}</div>
                  <div className="text-[10px] font-mono text-white/35 uppercase tracking-widest">
                    {lang === "bg" ? "Родител" : "Parent"}
                  </div>
                </div>
              </div>
              <div className="hr-soft my-4" />
              <div className="space-y-2 text-xs font-mono">
                <Row k={lang === "bg" ? "Имейл" : "Email"} v={form.email} />
                <Row k={lang === "bg" ? "Телефон" : "Phone"} v={form.phone} />
                <Row k={lang === "bg" ? "Град" : "City"} v={form.city} />
                <Row k={lang === "bg" ? "Деца" : "Children"} v={`${childCount}`} />
              </div>
            </div>
            <p className="text-[10px] font-mono text-white/30 mt-2 text-center">
              {lang === "bg" ? "Треньорите виждат това при чат и резервация" : "Coaches see this when chatting and booking"}
            </p>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, v, on, type = "text", testId, icon: Icon, placeholder, disabled }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />}
        <input
          type={type}
          value={v ?? ""}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => on(e.target.value)}
          className={`w-full bg-[#0A1628] border border-white/10 ${Icon ? "pl-9" : "px-3"} pr-3 py-2.5 outline-none focus:border-pitch disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-white/5 py-1.5">
      <span className="text-white/35 uppercase tracking-widest text-[10px]">{k}</span>
      <span className="text-white text-right truncate max-w-[60%]">{v || "—"}</span>
    </div>
  );
}
